import * as d3 from "d3";

/**
 * viz-art — PriceInspectionChart : graphe d'inspection des prix d'une carte TCG
 * (lignes mean + bougies min/max), agrégé par série (source × type).
 *
 * Compatible navigateur ET JSDOM (rendu côté serveur via viz-art-server).
 *
 * v0.2 (AZ-49 / AZ-48).
 */

// ─── Types publics ────────────────────────────────────────────────────────

export interface PricePoint {
  /** Bucket date au format YYYY-MM-DD. */
  date_bucket: string;
  mean_price: number;
  min_price: number;
  max_price: number;
  count: number;
}

export interface PriceSeries {
  source: string;
  type: "graded" | "ungraded";
  points: PricePoint[];
}

export interface PriceInspectionData {
  card_id: string;
  currency: string;
  /** IANA tz name pour le formattage tooltip. Défaut "Europe/Paris". */
  timezone?: string;
  series: PriceSeries[];
}

export interface PriceInspectionOptions {
  width?: number;
  height?: number;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  /** Largeur d'un body de bougie (px). Défaut 6. */
  candleBodyWidth?: number;
  /** Affiche la légende. Défaut true. */
  showLegend?: boolean;
  /** Affiche le tooltip au hover. Défaut true. */
  showTooltip?: boolean;
  /** Active ResizeObserver. Défaut true (auto-disabled en JSDOM). */
  responsive?: boolean;
}

// ─── Palette catégorielle 16 teintes (fond clair) ─────────────────────────

const PALETTE_16 = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728",
  "#9467bd", "#8c564b", "#e377c2", "#17becf",
  "#bcbd22", "#393b79", "#637939", "#8c6d31",
  "#843c39", "#7b4173", "#5254a3", "#a55194",
];

/**
 * Assigne une couleur stable par source (ordre alphabétique pour la stabilité
 * inter-renders). Au-delà de 16 sources, variation de luminosité contrôlée.
 */
function buildPalette(sources: string[]): Map<string, string> {
  const sorted = [...new Set(sources)].sort();
  const m = new Map<string, string>();
  sorted.forEach((src, i) => {
    if (i < PALETTE_16.length) {
      m.set(src, PALETTE_16[i]!);
    } else {
      const base = PALETTE_16[i % PALETTE_16.length]!;
      const k = Math.floor(i / PALETTE_16.length); // 1, 2, …
      const adjusted = k % 2 === 1
        ? d3.color(base)?.darker(0.6 * Math.ceil(k / 2)).formatHex()
        : d3.color(base)?.brighter(0.5 * (k / 2)).formatHex();
      m.set(src, adjusted ?? base);
    }
  });
  return m;
}

// ─── Helpers internes ─────────────────────────────────────────────────────

/** Parse "YYYY-MM-DD" en Date (UTC noon pour éviter DST edge cases). */
function parseDate(s: string): Date {
  return new Date(`${s}T12:00:00Z`);
}

interface CandleColor {
  /** "up" (green), "down" (red), "flat" (neutral) selon mean[n+1] vs mean[n]. */
  direction: "up" | "down" | "flat";
}

/** Détermine la couleur (direction) d'une bougie selon le point suivant de la série. */
function classifyCandles(points: PricePoint[]): CandleColor[] {
  return points.map((p, i) => {
    if (i === points.length - 1) return { direction: "flat" };
    const next = points[i + 1]!;
    if (next.mean_price < p.mean_price) return { direction: "down" };
    if (next.mean_price > p.mean_price) return { direction: "up" };
    return { direction: "flat" };
  });
}

const COLOR_UP = "#16a34a";   // green-600
const COLOR_DOWN = "#dc2626"; // red-600
const COLOR_FLAT = "#6b7280"; // gray-500

function bodyFillFor(c: CandleColor): string {
  if (c.direction === "up") return COLOR_UP;
  if (c.direction === "down") return COLOR_DOWN;
  return COLOR_FLAT;
}

// ─── Class principale ─────────────────────────────────────────────────────

export class PriceInspectionChart {
  private container: HTMLElement;
  private opts: Required<PriceInspectionOptions>;
  private resizeObserver: ResizeObserver | null = null;
  private data: PriceInspectionData | null = null;
  private palette: Map<string, string> = new Map();
  private svgEl: SVGSVGElement | null = null;
  private tooltipEl: HTMLDivElement | null = null;

  constructor(container: HTMLElement, options: PriceInspectionOptions = {}) {
    this.container = container;
    this.opts = {
      width: options.width ?? 800,
      height: options.height ?? 400,
      marginTop: options.marginTop ?? 30,
      marginRight: options.marginRight ?? 180, // espace légende
      marginBottom: options.marginBottom ?? 40,
      marginLeft: options.marginLeft ?? 60,
      candleBodyWidth: options.candleBodyWidth ?? 6,
      showLegend: options.showLegend ?? true,
      showTooltip: options.showTooltip ?? true,
      responsive: options.responsive ?? true,
    };
    this.initDom();
  }

  /** Render initial / re-render avec nouvelles données. */
  update(data: PriceInspectionData): void {
    this.data = data;
    const sources = data.series.map((s) => s.source);
    this.palette = buildPalette(sources);
    this.render();
  }

  /** Cleanup : enlève SVG, tooltip, observers. */
  destroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.container.innerHTML = "";
    this.svgEl = null;
    this.tooltipEl = null;
    this.data = null;
  }

  /** Snapshot du SVG sérialisé (pour rendu serveur). */
  toSvgString(): string {
    if (!this.svgEl) return "";
    const view = this.container.ownerDocument.defaultView as (Window & typeof globalThis) | null;
    const Ser = (view?.XMLSerializer ?? (globalThis as any).XMLSerializer) as typeof XMLSerializer | undefined;
    if (!Ser) return this.svgEl.outerHTML ?? "";
    return new Ser().serializeToString(this.svgEl);
  }

  // ─── DOM init ────────────────────────────────────────────────────────────

  private initDom() {
    this.container.innerHTML = "";
    this.container.style.position = this.container.style.position || "relative";
    this.container.style.fontFamily = this.container.style.fontFamily || "system-ui, sans-serif";

    if (this.opts.showTooltip) {
      const tt = this.container.ownerDocument.createElement("div");
      tt.style.position = "absolute";
      tt.style.pointerEvents = "none";
      tt.style.background = "rgba(17, 24, 39, 0.95)"; // gray-900
      tt.style.color = "white";
      tt.style.padding = "6px 8px";
      tt.style.borderRadius = "4px";
      tt.style.fontSize = "12px";
      tt.style.lineHeight = "1.3";
      tt.style.display = "none";
      tt.style.zIndex = "10";
      tt.style.maxWidth = "240px";
      this.container.appendChild(tt);
      this.tooltipEl = tt;
    }

    // ResizeObserver only in browser (JSDOM expose pas l'API).
    if (this.opts.responsive && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.data) this.render();
      });
      this.resizeObserver.observe(this.container);
    }
  }

  // ─── Render principal ───────────────────────────────────────────────────

  private render() {
    if (!this.data) return;

    const doc = this.container.ownerDocument;
    // Clear precedent SVG (pas le tooltip).
    if (this.svgEl) this.svgEl.remove();

    // Détecte taille effective (dimensions options par défaut ; getBoundingClientRect en browser pour responsive).
    let { width, height } = this.opts;
    const rect = this.container.getBoundingClientRect?.();
    if (rect && rect.width > 0 && this.opts.responsive) {
      width = rect.width;
      height = Math.max(this.opts.height, Math.round(rect.width * 0.5));
    }

    const svg = d3.select(doc.createElementNS("http://www.w3.org/2000/svg", "svg") as SVGSVGElement)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("background", "white")
      .style("font", "12px system-ui, sans-serif");

    this.svgEl = svg.node();
    this.container.insertBefore(this.svgEl!, this.tooltipEl ?? null);

    // État vide
    const allPoints = this.data.series.flatMap((s) => s.points);
    if (this.data.series.length === 0 || allPoints.length === 0) {
      svg.append("text")
        .attr("x", width / 2).attr("y", height / 2)
        .attr("text-anchor", "middle").attr("dominant-baseline", "middle")
        .attr("fill", "#6b7280")
        .text("Aucune donnée");
      return;
    }

    const m = this.opts;
    const innerW = width - m.marginLeft - m.marginRight;
    const innerH = height - m.marginTop - m.marginBottom;

    const x = d3.scaleTime()
      .domain(d3.extent(allPoints, (p) => parseDate(p.date_bucket)) as [Date, Date])
      .range([0, innerW])
      .nice();

    const yMin = d3.min(allPoints, (p) => p.min_price) ?? 0;
    const yMax = d3.max(allPoints, (p) => p.max_price) ?? 1;
    const y = d3.scaleLinear()
      .domain([Math.max(0, yMin * 0.95), yMax * 1.05])
      .range([innerH, 0])
      .nice();

    const root = svg.append("g")
      .attr("transform", `translate(${m.marginLeft},${m.marginTop})`);

    // Axes
    root.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .call(d3.axisBottom(x).tickFormat((d) => d3.timeFormat("%Y-%m-%d")(d as Date)) as any)
      .selectAll("text").attr("font-size", 11);

    root.append("g")
      .call(d3.axisLeft(y).tickFormat((v) => `${v}`) as any)
      .selectAll("text").attr("font-size", 11);

    // Y label : currency
    root.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -innerH / 2).attr("y", -45)
      .attr("text-anchor", "middle").attr("fill", "#374151")
      .text(`Prix (${this.data.currency})`);

    // Séries : lignes + bougies
    const seriesGroup = root.append("g").attr("class", "viz-series");
    for (const s of this.data.series) {
      this.drawSeries(seriesGroup, s, x, y);
    }

    // Légende
    if (this.opts.showLegend) {
      this.drawLegend(svg, width, height, root);
    }
  }

  // ─── Une série (ligne + bougies) ─────────────────────────────────────────

  private drawSeries(
    parent: d3.Selection<SVGGElement, unknown, null, undefined>,
    series: PriceSeries,
    x: d3.ScaleTime<number, number>,
    y: d3.ScaleLinear<number, number>
  ) {
    const baseColor = this.palette.get(series.source) ?? COLOR_FLAT;
    const isGraded = series.type === "graded";
    const lineOpacity = isGraded ? 1 : 0.75;
    const dashArray = isGraded ? "" : "4 3";

    const seriesId = `${series.source}__${series.type}`;
    const g = parent.append("g")
      .attr("class", `viz-serie viz-serie-${cssEscape(seriesId)}`)
      .attr("data-series-id", seriesId);

    if (series.points.length === 0) return;

    // Ligne mean
    const line = d3.line<PricePoint>()
      .x((p) => x(parseDate(p.date_bucket)))
      .y((p) => y(p.mean_price));

    g.append("path")
      .attr("d", line(series.points))
      .attr("fill", "none")
      .attr("stroke", baseColor)
      .attr("stroke-width", 1.8)
      .attr("stroke-opacity", lineOpacity)
      .attr("stroke-dasharray", dashArray);

    // Bougies (wick + body)
    const candleColors = classifyCandles(series.points);
    const bodyW = this.opts.candleBodyWidth;

    const candleG = g.append("g").attr("class", "viz-candles");
    for (let i = 0; i < series.points.length; i++) {
      const p = series.points[i]!;
      const c = candleColors[i]!;
      const cx = x(parseDate(p.date_bucket));

      // Wick (segment min → max)
      candleG.append("line")
        .attr("x1", cx).attr("x2", cx)
        .attr("y1", y(p.min_price)).attr("y2", y(p.max_price))
        .attr("stroke", baseColor)
        .attr("stroke-width", 1)
        .attr("stroke-opacity", lineOpacity);

      // Body : rect centré sur mean (hauteur fixe ~6px pour visibilité, élargi si min/max == mean)
      const meanY = y(p.mean_price);
      const bodyH = 6;
      const rect = candleG.append("rect")
        .attr("x", cx - bodyW / 2)
        .attr("y", meanY - bodyH / 2)
        .attr("width", bodyW)
        .attr("height", bodyH)
        .attr("fill", bodyFillFor(c))
        .attr("fill-opacity", isGraded ? 0.85 : 0.65)
        .attr("stroke", baseColor)
        .attr("stroke-width", 1.2)
        .attr("data-source", series.source)
        .attr("data-type", series.type)
        .attr("data-date", p.date_bucket)
        .attr("data-mean", String(p.mean_price))
        .attr("data-min", String(p.min_price))
        .attr("data-max", String(p.max_price))
        .attr("data-count", String(p.count));

      // Tooltip handlers (pas de transitions = JSDOM-safe)
      if (this.tooltipEl) {
        rect.style("cursor", "pointer");
        rect.on("mouseover", (ev) => this.showTooltip(ev as MouseEvent, series, p));
        rect.on("mousemove", (ev) => this.moveTooltip(ev as MouseEvent));
        rect.on("mouseout", () => this.hideTooltip());
      }
    }
  }

  // ─── Légende ─────────────────────────────────────────────────────────────

  private drawLegend(
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    width: number,
    _height: number,
    rootG: d3.Selection<SVGGElement, unknown, null, undefined>
  ) {
    if (!this.data) return;
    const xLeg = width - this.opts.marginRight + 20;
    const yStart = this.opts.marginTop;
    const lineH = 18;

    const legend = svg.append("g")
      .attr("class", "viz-legend")
      .attr("transform", `translate(${xLeg},${yStart})`);

    this.data.series.forEach((s, i) => {
      const baseColor = this.palette.get(s.source) ?? COLOR_FLAT;
      const isGraded = s.type === "graded";
      const seriesId = `${s.source}__${s.type}`;

      const row = legend.append("g")
        .attr("transform", `translate(0,${i * lineH})`)
        .style("cursor", "pointer")
        .on("mouseover", () => this.highlightSeries(rootG, seriesId))
        .on("mouseout", () => this.highlightSeries(rootG, null));

      row.append("line")
        .attr("x1", 0).attr("x2", 18)
        .attr("y1", 6).attr("y2", 6)
        .attr("stroke", baseColor).attr("stroke-width", 2)
        .attr("stroke-dasharray", isGraded ? "" : "4 3")
        .attr("stroke-opacity", isGraded ? 1 : 0.75);

      row.append("text")
        .attr("x", 24).attr("y", 9)
        .attr("font-size", 12).attr("fill", "#374151")
        .text(`${s.source} — ${s.type}`);
    });
  }

  /** Highlight d'une série au hover légende. */
  private highlightSeries(
    rootG: d3.Selection<SVGGElement, unknown, null, undefined>,
    seriesId: string | null
  ) {
    rootG.selectAll<SVGGElement, unknown>(".viz-serie")
      .style("opacity", function (this: SVGGElement) {
        if (!seriesId) return 1;
        return (this as SVGGElement).getAttribute("data-series-id") === seriesId ? 1 : 0.15;
      });
  }

  // ─── Tooltip ─────────────────────────────────────────────────────────────

  private showTooltip(ev: MouseEvent, series: PriceSeries, p: PricePoint) {
    if (!this.tooltipEl || !this.data) return;
    const tz = this.data.timezone ?? "Europe/Paris";
    let dateLabel = p.date_bucket;
    try {
      dateLabel = new Intl.DateTimeFormat("fr-FR", {
        timeZone: tz, day: "2-digit", month: "short", year: "numeric",
      }).format(parseDate(p.date_bucket));
    } catch { /* JSDOM peut ne pas avoir Intl complet */ }

    const fmt = (n: number) => `${n.toFixed(2)} ${this.data?.currency ?? ""}`;
    this.tooltipEl.innerHTML = `
      <div style="font-weight:600">${escapeHtml(series.source)} — ${series.type}</div>
      <div>${escapeHtml(dateLabel)}</div>
      <div>moy : ${fmt(p.mean_price)}</div>
      <div>min : ${fmt(p.min_price)} · max : ${fmt(p.max_price)}</div>
      <div style="opacity:.8">n = ${p.count}</div>
    `;
    this.tooltipEl.style.display = "block";
    this.moveTooltip(ev);
  }

  private moveTooltip(ev: MouseEvent) {
    if (!this.tooltipEl) return;
    const rect = this.container.getBoundingClientRect?.();
    const offsetX = rect ? ev.clientX - rect.left + 12 : 12;
    const offsetY = rect ? ev.clientY - rect.top + 12 : 12;
    this.tooltipEl.style.left = `${offsetX}px`;
    this.tooltipEl.style.top = `${offsetY}px`;
  }

  private hideTooltip() {
    if (this.tooltipEl) this.tooltipEl.style.display = "none";
  }
}

// ─── Helper backend (JSDOM) ───────────────────────────────────────────────

/**
 * Rendu serveur : le caller fournit { document, window } d'un jsdom synthétique.
 * Retourne le SVG sérialisé + dimensions + meta (palette, count).
 *
 * Usage côté server :
 *   const dom = new JSDOM("<div id='c'></div>");
 *   const c = dom.window.document.getElementById("c");
 *   const out = renderPriceInspectionToString(dom.window, c, data, opts);
 */
export function renderPriceInspectionToString(
  _win: { document: Document },
  container: HTMLElement,
  data: PriceInspectionData,
  options?: PriceInspectionOptions
): { svg: string; width: number; height: number; meta: { palette: Record<string, string>; series_count: number } } {
  const chart = new PriceInspectionChart(container, { ...options, responsive: false });
  chart.update(data);
  const svg = chart.toSvgString();
  const w = options?.width ?? 800;
  const h = options?.height ?? 400;
  const palette: Record<string, string> = {};
  // Reconstruire palette pour meta
  const sources = data.series.map((s) => s.source);
  const map = buildPalette(sources);
  map.forEach((v, k) => { palette[k] = v; });
  // Note: ne pas destroy ici pour éviter de wipe le DOM caller — caller peut destroy lui-même.
  return { svg, width: w, height: h, meta: { palette, series_count: data.series.length } };
}

// ─── Utils ──────────────────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function cssEscape(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]/g, "_");
}
